<script>
import ProductHelper from "@/utils/productHelper";
import ItemCard from "./ItemCard";

export default {
  components: { ItemCard },
  props: {
    kit: { type: Object, required: true },
    currentStep: { type: Object, default: null },
    alertMode: { type: Boolean, default: false },
    showCompleted: { type: Boolean, default: true },
    inlineDetailProductId: { type: String, default: null },
    config: { type: Object, default: null },
  },
  computed: {
    visibleChildren() {
      return this.kit.products.filter((p) =>
        ProductHelper.isProductVisible(p, this.currentStep, this.showCompleted),
      );
    },
    eligibleChildren() {
      return this.kit.products.filter(
        (p) =>
          !ProductHelper.isProductStepCompleted(p, this.currentStep) &&
          !(
            ProductHelper.hasNoProductStep(p, this.currentStep) ||
            ProductHelper.isProductDamaged(p) ||
            ProductHelper.isProductBackOrder(p)
          ),
      );
    },
    isKitCompleted() {
      if (!this.currentStep) return true;
      return this.eligibleChildren.length === 0;
    },
    hasDamaged() {
      return this.kit.products.some((p) => p.isDamaged);
    },
    hasBackOrder() {
      return this.kit.products.some((p) => p.isBackOrder);
    },
    isAlerted() {
      return this.hasDamaged || this.hasBackOrder;
    },
    isDimmed() {
      return this.alertMode && !this.isAlerted;
    },
    collapseId() {
      return "kit-" + this.kit.name.replace(/\s+/g, "-").toLowerCase();
    },
    childCount() {
      return this.kit.products.length;
    },
  },
  methods: {
    onKitCheck() {
      if (this.isKitCompleted) return;
      this.$emit("kit-check", this.eligibleChildren);
    },
    onChildCheck(product) {
      this.$emit("child-check", product);
    },
    onChildOpenAlert(product) {
      this.$emit("child-open-alert", product);
    },
    onChildToggleInlineDetail(product) {
      this.$emit("child-toggle-inline-detail", product);
    },
    onChildCardClick(product) {
      this.$emit("child-card-click", product);
    },
  },
};
</script>
<template>
  <div
    v-if="visibleChildren.length > 0"
    class="kcard"
    :class="{ scanned: isKitCompleted, 'dmg-hl': isAlerted, dim: isDimmed }"
  >
    <div class="kmain">
      <div class="kico">
        <i class="mdi custom-kit"></i>
      </div>
      <div class="kinfo">
        <div class="kname">{{ kit.name }}</div>
        <div class="kenv">
          <strong>{{ $t("general.products") }}:</strong>
          {{ childCount }}
        </div>
      </div>
      <div class="kright">
        <span
          v-if="hasDamaged"
          class="abubble abubble-dmg"
          :title="$t('general.damaged')"
        >
          <i class="mdi mdi-alert-outline"></i>
        </span>
        <span
          v-if="hasBackOrder"
          class="abubble abubble-bo"
          :title="$t('general.bo')"
        >
          <i class="mdi mdi-minus-circle-outline"></i>
        </span>
        <span
          class="chkbox"
          :class="{ checked: isKitCompleted }"
          role="button"
          @click.stop="onKitCheck"
        >
          <i class="mdi mdi-check" v-if="isKitCompleted"></i>
        </span>
        <button v-b-toggle="collapseId" class="expbtn" @click.stop>
          <i class="mdi mdi-chevron-down expbtn-icon"></i>
        </button>
      </div>
    </div>

    <b-collapse :id="collapseId">
      <div class="kchildren">
        <ItemCard
          v-for="p in visibleChildren"
          :key="p.idPublic"
          :product="p"
          :current-step="currentStep"
          :alert-mode="alertMode"
          :inline-detail-open="inlineDetailProductId === p.idPublic"
          :config="config"
          @card-click="onChildCardClick(p)"
          @check="onChildCheck(p)"
          @toggle-inline-detail="onChildToggleInlineDetail(p)"
          @open-alert="onChildOpenAlert(p)"
        />
      </div>
    </b-collapse>
  </div>
</template>
<style scoped>
.kcard {
  background: white;
  border-radius: 11px;
  border: 1.5px solid #e0e5ea;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s, opacity 0.2s;
}
.kcard.scanned {
  background: #e6faf3;
  border-color: #a9dfc3;
}
.kcard.dim {
  opacity: 0.28;
}
.kmain {
  padding: 9px 11px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
}
.kico {
  width: 30px;
  height: 30px;
  background: #fff4dd;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f1b44c;
  font-size: 18px;
  flex-shrink: 0;
}
.kcard.scanned .kico {
  background: rgba(52, 195, 143, 0.12);
  color: #34c38f;
}
.kinfo {
  flex: 1;
  min-width: 0;
}
.kname {
  font-size: 12px;
  font-weight: 700;
  color: #1c2e3e;
}
.kenv {
  font-size: 11px;
  color: #7a8a99;
  margin-top: 1px;
}
.kenv strong {
  color: #1c2e3e;
  font-weight: 600;
}
.kright {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.abubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  flex-shrink: 0;
  font-size: 15px;
}
.abubble-dmg {
  background: #fff8e1;
  color: #f1b44c;
  border: 1px solid #f9d77e;
}
.abubble-bo {
  background: #fdecea;
  color: #e74c3c;
  border: 1px solid #f9bdbd;
}
.chkbox {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1.5px solid #e0e5ea;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
}
.chkbox.checked {
  background: #34c38f;
  border-color: #34c38f;
  color: white;
}
.expbtn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e0e5ea;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a8a99;
  font-size: 13px;
  padding: 0;
}
.expbtn-icon {
  transition: transform 0.2s;
}
.expbtn.not-collapsed .expbtn-icon {
  transform: rotate(180deg);
}
.kchildren {
  padding: 0 11px 9px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  border-top: 1px solid #e0e5ea;
  padding-top: 9px;
}
.kcard.scanned .kchildren {
  border-top-color: #a9dfc3;
}
</style>
